// Login — the dummy-login participant picker (INTEGRATION.md §4). There is no
// real auth this phase: the backend reads identity from the X-User-Id header, so
// "logging in" means choosing which sample participant to be. We populate the
// list from GET /users, and selecting one calls auth.login() (which sets the
// header + bootstraps /me) then routes into the app. When SAML replaces the
// header, this screen is removed and the auth-context stays the same.
//
// No login screen exists in the prototype, so this is built in the same design
// language (Pearl surfaces, brand tokens, Avatar) as the rest of the portal.
import { useEffect, useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Surface, Avatar, Spinner, ErrorState, EmptyState } from '../../components/reusable';
import { useAuth } from '../../auth/AuthProvider';
import { SparkleIcon } from '../../components/icons/Icons';
import './login.css';

// Cycle the brand palette so each participant tile reads distinctly.
const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #0077cc 0%, #002266 100%)',
  'linear-gradient(135deg, #119944 0%, #006633 100%)',
  'linear-gradient(135deg, #552299 0%, #002266 100%)',
  'linear-gradient(135deg, #ff6600 0%, #cc0000 100%)',
];

// Plan-count capsule tone, aligned 1:1 with AVATAR_GRADIENTS so each capsule
// matches its tile's avatar color.
const AVATAR_TONES = ['blue', 'green', 'purple', 'orange'];

const fullName = (u) => [u.firstName, u.lastName].filter(Boolean).join(' ') || u.participantId;

export function Login() {
  // Identity + the picker list both come from the auth slice (via useAuth).
  const { login, isAuthenticated, users, usersStatus, usersError, loadUsers } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // After login, go to "/" — the redirect-only index, which sends a participant to
  // their Dashboard (on their default plan) and a sponsor to the plan picker. A
  // deep-link redirect (from a protected route) still returns the user to where
  // they were headed.
  const redirectTo = location.state?.from?.pathname || '/';

  // participantId currently being signed in (drives the per-tile spinner).
  const [signingIn, setSigningIn] = useState(null);
  const [loginError, setLoginError] = useState(null);

  const loading = usersStatus === 'idle' || usersStatus === 'loading';
  const error = usersStatus === 'failed' ? usersError : null;

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handlePick = async (participantId) => {
    setSigningIn(participantId);
    setLoginError(null);
    try {
      await login(participantId);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setLoginError(err.message || 'Sign-in failed. Please try again.');
      setSigningIn(null);
    }
  };

  // Already signed in (e.g. navigated here manually) → go to the app.
  if (isAuthenticated) return <Navigate to={redirectTo} replace />;

  return (
    <div className="login-page">
      <Surface className="login-card" padding="none">
        <div className="login-brand">
          <span className="login-brand__mark"><SparkleIcon width={18} height={18} /></span>
          <span className="login-brand__name">PCS Retirement</span>
        </div>
        <h1 className="login-title">Welcome</h1>
        <p className="login-sub">Choose a participant to sign in to the demo portal.</p>

        {loginError && <div className="login-error" role="alert">{loginError}</div>}

        {loading && (
          <div className="login-state"><Spinner size="lg" label="Loading participants" /></div>
        )}

        {!loading && error && (
          <div className="login-state">
            <ErrorState message={error} onRetry={loadUsers} />
          </div>
        )}

        {!loading && !error && users && users.length === 0 && (
          <div className="login-state">
            <EmptyState
              title="No participants available"
              message="The backend returned no sample participants to sign in as."
            />
          </div>
        )}

        {!loading && !error && users && users.length > 0 && (
          <ul className="login-list">
            {users.map((u, i) => {
              const busy = signingIn === u.participantId;
              return (
                <li key={u.participantId}>
                  <button
                    type="button"
                    className="login-user"
                    onClick={() => handlePick(u.participantId)}
                    disabled={signingIn != null}
                    aria-busy={busy}
                  >
                    <Avatar name={fullName(u)} gradient={AVATAR_GRADIENTS[i % AVATAR_GRADIENTS.length]} />
                    <span className="login-user__text">
                      <span className="login-user__name-row">
                        <span className="login-user__name">{fullName(u)}</span>
                        {u.planCount != null && (
                          <span className={`login-user__plans login-user__plans--${AVATAR_TONES[i % AVATAR_TONES.length]}`}>
                            {u.planCount} {u.planCount === 1 ? 'plan' : 'plans'}
                          </span>
                        )}
                      </span>
                      <span className="login-user__meta">{u.planName || u.participantId}</span>
                    </span>
                    {busy
                      ? <Spinner size="sm" />
                      : <span className="login-user__arrow" aria-hidden="true">→</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <p className="login-note">
          Demo sign-in — identity is sent as <code>X-User-Id</code>. No password required.
        </p>
      </Surface>
    </div>
  );
}

export default Login;
